import { useState, type ComponentType } from 'react'
import { Link } from 'react-router'
import { ArrowRight, CheckIcon } from 'lucide-react'
import { Reveal } from '@/components/motion/Reveal'
import { AnimatedCounter } from '@/components/motion/AnimatedCounter'
import { useCatalogPlans, useCurrencies, usePlanPrices } from '../hooks'
import { BillingToggle, CurrencySelect, type BillingCadence } from '../components/PlanControls'
import { PlanGrid } from '../components/PlanGrid'
import {
  PortalPreviewVignette,
  SsoLoginVignette,
  SubscriptionLifecycleVignette,
  WebhookFeedVignette,
} from '../components/vignettes'

/**
 * P1 landing page (UI_PLAN Screen 1, mockup "FenceAuth Public Site") with the
 * vibrancy pass: animated hero, logo marquee, live product vignettes in the
 * feature blocks, count-up stats, and staggered scroll reveals — all honoring
 * prefers-reduced-motion.
 */

function Hero() {
  return (
    <section className="sidebar-texture relative overflow-hidden bg-sidebar px-5 py-14 sm:px-10 sm:py-16 lg:px-14">
      {/* Breathing glow + drifting gradient bars (decorative) */}
      <div
        className="animate-glow pointer-events-none absolute -top-32 left-1/2 h-[420px] w-[680px] -translate-x-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgb(20_184_166/0.18),transparent_65%)]"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -top-20 right-4 hidden gap-5 opacity-50 lg:flex"
        aria-hidden
      >
        <span className="animate-float h-[380px] w-7 rounded-[14px] bg-gradient-to-b from-accent/50 to-transparent" />
        <span className="animate-float-slow h-[480px] w-7 rounded-[14px] bg-gradient-to-b from-white/15 to-transparent" />
        <span className="animate-float h-[330px] w-7 rounded-[14px] bg-gradient-to-b from-white/10 to-transparent [animation-delay:1.5s]" />
        <span className="animate-float-slow h-[430px] w-7 rounded-[14px] bg-gradient-to-b from-white/[0.07] to-transparent [animation-delay:3s]" />
      </div>
      <div className="relative mx-auto grid w-full max-w-6xl items-center gap-10 lg:grid-cols-[1.15fr_1fr] lg:gap-14">
        <div>
          <p className="mb-6 inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/10 px-3 py-1.5">
            <span className="animate-status-ping size-1.5 rounded-full bg-accent" aria-hidden />
            <span className="text-[11.5px] font-semibold text-accent-on-dark">
              v2 out now · multi-currency pricing &amp; SCIM groups
            </span>
          </p>
          <h1 className="mb-5 text-4xl leading-[1.05] font-semibold tracking-tight text-white sm:text-5xl lg:text-[54px]">
            Auth and billing infrastructure for SaaS
          </h1>
          <p className="mb-8 max-w-lg text-base leading-relaxed text-[#94A3B8] sm:text-[17.5px]">
            Users, subscriptions, entitlements and a white-label customer portal — across every
            product you ship. One API, one dashboard, no billing rebuild.
          </p>
          <div className="mb-6 flex flex-wrap items-center gap-3">
            <Link
              to="/signup"
              className="flex h-[50px] items-center rounded-[11px] bg-accent px-6 text-[15px] font-semibold text-accent-ink transition-all hover:-translate-y-0.5 hover:bg-accent/90 hover:shadow-[0_12px_30px_rgb(20_184_166/0.35)] motion-reduce:transition-none motion-reduce:hover:translate-y-0"
            >
              Start free — 14 days
            </Link>
            <Link
              to="/pricing"
              className="flex h-[50px] items-center gap-2 rounded-[11px] border border-white/20 px-5 text-[14.5px] font-semibold text-white transition-colors hover:bg-white/5"
            >
              View pricing <ArrowRight className="size-3.5" aria-hidden />
            </Link>
          </div>
          <p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#7C8CA6]">
            <span>No card required</span>
            <span className="size-[3px] rounded-full bg-[#3C4A62]" aria-hidden />
            <span>Stub-mode sandbox</span>
            <span className="size-[3px] rounded-full bg-[#3C4A62]" aria-hidden />
            <span>SOC 2 Type II</span>
          </p>
        </div>

        <div className="hidden rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-sm md:block">
          <div className="mb-3.5 flex items-center gap-2">
            <span className="rounded-[7px] bg-white/10 px-2.5 py-1.5 font-mono text-[11px] font-semibold text-white">
              cURL
            </span>
            <span className="px-2.5 py-1.5 font-mono text-[11px] text-sidebar-muted">Node</span>
            <span className="px-2.5 py-1.5 font-mono text-[11px] text-sidebar-muted">Python</span>
          </div>
          <pre className="font-mono text-xs leading-[1.9] whitespace-pre-wrap text-[#8FA0B8]">
            <span className="text-[#7C8CA6]"># can this user export data?</span>
            {'\ncurl '}
            <span className="text-accent-on-dark">
              https://api.fenceauth.com/v1/{'\n  '}entitlements/me/product/vault
            </span>
            {' \\\n  -H '}
            <span className="text-console-accent">&quot;Authorization: Bearer $FA_KEY&quot;</span>
            <span className="animate-caret-blink ml-0.5 inline-block h-3 w-1.5 bg-accent/70 align-middle" />
          </pre>
          <pre className="mt-3.5 rounded-[10px] border border-white/[0.07] bg-console px-3.5 py-3 font-mono text-[11.5px] leading-[1.85] text-[#8FA0B8]">
            {'{\n  '}
            <span className="text-accent-on-dark">&quot;plan&quot;</span>:{' '}
            <span className="text-console-accent">&quot;vault-pro&quot;</span>
            {',\n  '}
            <span className="text-accent-on-dark">&quot;state&quot;</span>:{' '}
            <span className="text-console-accent">&quot;ACTIVE&quot;</span>
            {',\n  '}
            <span className="text-accent-on-dark">&quot;exports&quot;</span>:{' '}
            <span className="text-white">true</span>
            {',\n  '}
            <span className="text-accent-on-dark">&quot;team_seats&quot;</span>:{' '}
            <span className="text-white">10</span>
            {'\n}'}
          </pre>
          <p className="mt-3 flex items-center gap-2 text-[11.5px] text-sidebar-muted">
            <span className="animate-status-ping size-1.5 rounded-full bg-accent" aria-hidden />
            p95 148 ms · 99.98% uptime last 90 days
          </p>
        </div>
      </div>
    </section>
  )
}

const TRUSTED_BY = ['NORTHWIND', 'brightpath', 'Westline', 'FIELDKIT', 'orbitworks', 'Vergegrid']

function LogoStrip() {
  return (
    <section className="overflow-hidden border-b border-white/5 bg-console py-5">
      <div className="mx-auto flex w-full max-w-6xl items-center gap-8 px-5 sm:px-10">
        <span className="shrink-0 text-[11px] font-medium tracking-widest whitespace-nowrap text-[#7C8CA6] uppercase">
          Trusted by product teams at
        </span>
        <div className="marquee-mask min-w-0 flex-1 overflow-hidden" data-testid="logo-marquee">
          <div className="animate-marquee flex w-max items-center gap-14 hover:[animation-play-state:paused]">
            {[...TRUSTED_BY, ...TRUSTED_BY].map((name, index) => (
              <span
                key={`${name}-${index}`}
                className="text-[15px] font-semibold whitespace-nowrap text-[#7C8CA6] transition-colors hover:text-[#8FA0B8]"
                aria-hidden={index >= TRUSTED_BY.length}
              >
                {name}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

const HOW_IT_WORKS = [
  {
    title: 'Define products & plans',
    body: 'Model what you sell once: cadence, prices per currency, trials, grace periods and the feature flags each plan grants.',
  },
  {
    title: 'Point your app at us',
    body: 'Send users to the hosted register and billing flows, or call the API directly. Sessions are server-side; your SPA never holds a token.',
  },
  {
    title: 'Gate features, not roles',
    body: 'Ask one question — does this user have this feature? Plan changes, trials and dunning all resolve to the same answer.',
  },
]

function HowItWorks() {
  return (
    <section className="border-b px-5 py-14 sm:px-10">
      <div className="mx-auto w-full max-w-6xl">
        <Reveal className="mb-9 text-center">
          <p className="mb-3 font-mono text-[11px] font-semibold tracking-widest text-accent-strong uppercase">
            How it works
          </p>
          <h2 className="mb-2.5 text-2xl font-semibold tracking-tight text-content sm:text-[32px]">
            Three moving parts, one afternoon
          </h2>
          <p className="mx-auto max-w-xl text-sm leading-relaxed text-content-secondary">
            You keep your product. We take identity, money and the account screens nobody wants to
            build twice.
          </p>
        </Reveal>
        <div className="grid gap-5 md:grid-cols-3">
          {HOW_IT_WORKS.map((step, index) => (
            <Reveal
              key={step.title}
              delay={index * 120}
              className="card-lift rounded-2xl border bg-surface-raised p-6"
            >
              <div className="mb-4 flex items-center gap-3">
                <span className="flex size-[30px] items-center justify-center rounded-lg bg-brand font-mono text-xs font-bold text-white">
                  {index + 1}
                </span>
                <h3 className="text-[15px] font-semibold text-content">{step.title}</h3>
              </div>
              <p className="text-[13px] leading-relaxed text-content-secondary">{step.body}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  )
}

interface FeatureBlock {
  badge: string
  badgeClass: string
  title: string
  body: string
  bullets: string[]
  vignette: ComponentType
  dark?: boolean
  vignetteFirst?: boolean
}

const FEATURE_BLOCKS: FeatureBlock[] = [
  {
    badge: 'IDENTITY',
    badgeClass: 'bg-brand-soft text-brand',
    title: 'Enterprise SSO without an enterprise project',
    body: 'OIDC and SAML providers per email domain, SCIM 2.0 provisioning, MFA policy, session lifetimes and per-user session revocation. Your login screen swaps to SSO on email blur — watch it happen.',
    bullets: [
      'Domain-claimed IdP routing with DNS verification',
      'Attribute mappers with a live test assertion',
      'Break-glass MFA bypass, fully audited',
    ],
    vignette: SsoLoginVignette,
  },
  {
    badge: 'BILLING',
    badgeClass: 'bg-accent-soft text-accent-strong',
    title: 'A subscription state machine you can trust',
    body: "Trials, conversions, pauses, plan changes with proration preview, dunning with grace periods, refunds under dual control. Only legal transitions ever render, so your support team can't create an impossible state.",
    bullets: [
      'Multi-currency prices, set per market not converted',
      'Optimistic locking on plan changes (If-Match)',
      'Idempotency keys on every financial write',
    ],
    vignette: SubscriptionLifecycleVignette,
    vignetteFirst: true,
  },
  {
    badge: 'WHITE-LABEL PORTAL',
    badgeClass: 'bg-status-success-bg text-status-success-fg',
    title: 'Account screens your customers think you built',
    body: 'Register, plan picker, invoices, payment methods, sessions, MFA recovery codes, data export, cancellation — on your domain, in your colours, sent from your email address.',
    bullets: [
      'Themed at runtime from one API call',
      'Custom domain with DNS verification',
      'Your SMTP for every identity email',
    ],
    vignette: PortalPreviewVignette,
  },
  {
    badge: 'OPERATIONS',
    badgeClass: 'bg-accent/15 text-accent-on-dark',
    title: 'Built to be debugged at 2am',
    body: 'Signed webhooks with a visible retry ladder, per-delivery payloads and copy-as-cURL, a hash-chained audit log, and an outbox with replay. When something breaks you get the request, not a status page.',
    bullets: [
      'Delivery log retained 30 days, exportable',
      'Published egress IPs for firewall rules',
      'Audit integrity check you can hand an auditor',
    ],
    vignette: WebhookFeedVignette,
    dark: true,
    vignetteFirst: true,
  },
]

function FeatureBlocks() {
  return (
    <section id="platform" className="border-b bg-surface-page px-5 py-14 sm:px-10">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
        <Reveal>
          <h2 className="text-2xl font-semibold tracking-tight text-content sm:text-[32px]">
            Four surfaces, one system of record
          </h2>
        </Reveal>
        {FEATURE_BLOCKS.map((block) => {
          const Vignette = block.vignette
          return (
            <Reveal
              key={block.badge}
              className={
                block.dark
                  ? 'sidebar-texture relative overflow-hidden rounded-2xl bg-sidebar p-7 sm:p-8'
                  : 'rounded-2xl border bg-surface-raised p-7 sm:p-8'
              }
            >
              <span
                className={`mb-4 inline-flex rounded-md px-2.5 py-1 font-mono text-[9.5px] font-bold tracking-wide ${block.badgeClass}`}
              >
                {block.badge}
              </span>
              <div className="grid items-center gap-7 lg:grid-cols-2 lg:gap-10">
                <div className={block.vignetteFirst ? 'lg:order-2' : ''}>
                  <h3
                    className={`mb-3 text-xl leading-snug font-semibold tracking-tight sm:text-2xl ${block.dark ? 'text-white' : 'text-content'}`}
                  >
                    {block.title}
                  </h3>
                  <p
                    className={`mb-4 text-[13.5px] leading-relaxed ${block.dark ? 'text-[#94A3B8]' : 'text-content-secondary'}`}
                  >
                    {block.body}
                  </p>
                  <ul className="flex flex-col gap-2.5">
                    {block.bullets.map((bullet) => (
                      <li
                        key={bullet}
                        className={`flex gap-2.5 text-[13px] ${block.dark ? 'text-console-fg' : 'text-content-body'}`}
                      >
                        <CheckIcon className="mt-0.5 size-3.5 shrink-0 text-accent" aria-hidden />
                        {bullet}
                      </li>
                    ))}
                  </ul>
                </div>
                <div
                  className={`flex justify-center ${block.vignetteFirst ? 'lg:order-1 lg:justify-start' : 'lg:justify-end'}`}
                >
                  <Vignette />
                </div>
              </div>
            </Reveal>
          )
        })}
      </div>
    </section>
  )
}

const STATS = [
  { value: '1.4M', label: 'end-users authenticated monthly' },
  { value: '148ms', label: 'p95 API latency, all regions' },
  { value: '99.98%', label: 'uptime over the last 90 days' },
  { value: '6 weeks', label: 'median time teams say they saved' },
]

function StatsBand() {
  return (
    <section className="grid grid-cols-2 border-b lg:grid-cols-4">
      {STATS.map((stat, index) => (
        <Reveal
          key={stat.value}
          delay={index * 100}
          className={`px-7 py-8 sm:px-9 ${index < STATS.length - 1 ? 'border-r border-line-subtle' : ''} ${index < 2 ? 'max-lg:border-b max-lg:border-line-subtle' : ''}`}
        >
          <p className="text-[28px] font-semibold tracking-tight text-content sm:text-[34px]">
            <AnimatedCounter value={stat.value} />
          </p>
          <p className="mt-2 text-xs leading-normal text-content-secondary">{stat.label}</p>
        </Reveal>
      ))}
    </section>
  )
}

function CustomerStory() {
  return (
    <section className="border-b px-5 py-14 sm:px-10">
      <Reveal className="mx-auto w-full max-w-4xl rounded-2xl border bg-surface-page p-8 sm:p-9">
        <p className="mb-4 font-mono text-[11px] font-semibold tracking-widest text-accent-strong uppercase">
          Customer story
        </p>
        <blockquote className="text-lg leading-relaxed font-semibold tracking-tight text-content sm:text-[22px]">
          &ldquo;We had SSO, SCIM and proration on the roadmap for two quarters. We shipped all
          three in a fortnight and deleted 11k lines of billing code.&rdquo;
        </blockquote>
        <div className="mt-5 flex items-center gap-3">
          <span className="flex size-10 items-center justify-center rounded-full bg-brand font-mono text-[13px] font-bold text-accent-on-dark">
            JR
          </span>
          <div>
            <p className="text-[13px] font-semibold text-content">Jordan Reyes</p>
            <p className="mt-0.5 text-[11.5px] text-content-secondary">
              VP Engineering, Northwind Labs
            </p>
          </div>
        </div>
        <div className="mt-6 grid grid-cols-1 gap-4 border-t pt-5 sm:grid-cols-3">
          {[
            { value: '11k', label: 'lines of billing code deleted' },
            { value: '14 days', label: 'to first enterprise SSO customer' },
            { value: '−38%', label: 'billing support tickets' },
          ].map((stat) => (
            <div key={stat.value}>
              <p className="text-[19px] font-semibold text-content">{stat.value}</p>
              <p className="mt-1 text-[11.5px] text-content-secondary">{stat.label}</p>
            </div>
          ))}
        </div>
      </Reveal>
    </section>
  )
}

function PricingSection() {
  const [cadence, setCadence] = useState<BillingCadence>('monthly')
  const [currencyCode, setCurrencyCode] = useState('USD')
  const plansQuery = useCatalogPlans()
  const currenciesQuery = useCurrencies()
  const pricesQuery = usePlanPrices(currencyCode)

  return (
    <section className="border-b bg-surface-page px-5 py-14 sm:px-10">
      <div className="mx-auto w-full max-w-6xl">
        <Reveal className="mb-7 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="mb-3 font-mono text-[11px] font-semibold tracking-widest text-accent-strong uppercase">
              Pricing
            </p>
            <h2 className="mb-2 text-2xl font-semibold tracking-tight text-content sm:text-[32px]">
              Every plan has the whole API
            </h2>
            <p className="text-sm text-content-secondary">
              You pay for scale, not for features you already needed on day one.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <BillingToggle value={cadence} onChange={setCadence} />
            <CurrencySelect
              currencies={currenciesQuery.data}
              value={currencyCode}
              onChange={setCurrencyCode}
            />
          </div>
        </Reveal>
        <Reveal delay={120}>
          <PlanGrid
            plans={plansQuery.data}
            prices={pricesQuery.data}
            currencyCode={currencyCode}
            cadence={cadence}
            isLoading={plansQuery.isLoading}
          />
        </Reveal>
        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border bg-surface-raised px-4 py-3.5">
          <p className="text-xs leading-relaxed text-content-secondary">
            Exceeding your MAU cap never breaks your app — new end-user registrations pause and we
            email you before it happens.
          </p>
          <Link
            to="/pricing"
            className="ml-auto text-xs font-semibold whitespace-nowrap text-accent-strong hover:underline"
          >
            Compare every feature →
          </Link>
        </div>
      </div>
    </section>
  )
}

function ClosingCta() {
  return (
    <section className="sidebar-texture relative overflow-hidden bg-sidebar px-5 py-12 sm:px-10">
      <div
        className="animate-glow pointer-events-none absolute -bottom-24 left-16 h-[280px] w-[420px] rounded-full bg-[radial-gradient(ellipse_at_center,rgb(20_184_166/0.16),transparent_65%)]"
        aria-hidden
      />
      <Reveal className="relative mx-auto flex w-full max-w-6xl flex-wrap items-center gap-8">
        <div className="min-w-0 flex-1 basis-72">
          <h2 className="mb-3 text-2xl font-semibold tracking-tight text-white sm:text-[34px]">
            Ship auth and billing this quarter
          </h2>
          <p className="max-w-lg text-sm leading-relaxed text-[#94A3B8] sm:text-[15px]">
            Free for 14 days, no card. Stub-mode sandbox so you can integrate against the real API
            before you decide.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to="/signup"
            className="flex h-[50px] items-center rounded-[11px] bg-accent px-5 text-[15px] font-semibold text-accent-ink transition-all hover:-translate-y-0.5 hover:bg-accent/90 hover:shadow-[0_12px_30px_rgb(20_184_166/0.35)] motion-reduce:transition-none motion-reduce:hover:translate-y-0"
          >
            Create your workspace
          </Link>
          <a
            href="mailto:sales@fenceauth.com"
            className="flex h-[50px] items-center rounded-[11px] border border-white/20 px-5 text-[14.5px] font-semibold text-white hover:bg-white/5"
          >
            Book a demo
          </a>
        </div>
      </Reveal>
    </section>
  )
}

export function LandingPage() {
  return (
    <div className="flex flex-col">
      <Hero />
      <LogoStrip />
      <HowItWorks />
      <FeatureBlocks />
      <StatsBand />
      <CustomerStory />
      <PricingSection />
      <ClosingCta />
    </div>
  )
}
